import { useState, useEffect, MouseEvent } from "react";
import { Modal, Form, Table, Button, Alert } from "react-bootstrap";
import { useAppSelector } from "../../../../redux/hooks";
import { lddGlobalSessionInfo } from "../../../navigationTypes";
import { saveCustomFieldSet, updateCustomFieldSet } from "./customFieldSetFuncs";
import {
    ALL_AUTO_APPLY_FILE_TYPES,
    AutoApplyFileType,
    ClientConnectCustomFieldSet,
    defaultFieldSet,
    MAX_INTAKE_FIELD_NAME_LENGTH,
    isIntakeFieldNameTooLong
} from "./types";

export type NewCustomFieldSetModalProps = {
    show: boolean;
    onHide: {
        (): void;
        (event: MouseEvent<HTMLButtonElement, MouseEvent>): void;
    };
    editFieldSet: ClientConnectCustomFieldSet,
    fromSettings: boolean
}

const getDefaultFieldSet = (): ClientConnectCustomFieldSet => {
    return {
        ...defaultFieldSet,
        fields: defaultFieldSet.fields.map(field => ({ ...field })),
        autoApplyFileTypes: []
    };
};

export const NewCustomFieldSetModal = (props: NewCustomFieldSetModalProps) => {
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);
    const [fieldSet, setFieldSet] = useState<ClientConnectCustomFieldSet>(getDefaultFieldSet());
    const [clear, setClear] = useState<boolean>(false);

    useEffect(() => {
        if (props.editFieldSet) {
            setFieldSet(props.editFieldSet)
        } else {
            for (let i = 0; i < fieldSet.fields.length; i++) {
                handleFieldCleared(i)
            }
            setFieldSet(getDefaultFieldSet())
        }
    }, [props.editFieldSet, props.show])


    const handleFieldChange = (index, event) => {
        const newFields = [...fieldSet.fields];
        newFields[index].name = event.target.value;
        setFieldSet({
            ...fieldSet,
            fields: newFields,
        });
    };

    const addNewField = () => {
        const newFields = [...fieldSet.fields, { name: '', type: 'text', perClient: false, value: '', fromSet: true, id: '-1', identifier: '' }];
        setFieldSet({
            ...fieldSet,
            fields: newFields,
        });
    };

    const handleFieldSetNameChange = (event) => {
        const newFieldSetName = event.target.value;
        setFieldSet({
            ...fieldSet,
            fieldSetName: newFieldSetName,
        })
    }

    const isBC = sessionInfo.appProvinceShort === 'BC';

    const availableFileTypes: AutoApplyFileType[] = ALL_AUTO_APPLY_FILE_TYPES.filter(
        ft => ft !== 'Family Transfer' || isBC
    );

    const handleAutoApplyFileTypeToggle = (fileType: AutoApplyFileType, checked: boolean) => {
        const current = fieldSet.autoApplyFileTypes ?? [];
        const next = checked
            ? [...current, fileType]
            : current.filter(ft => ft !== fileType);
        setFieldSet({
            ...fieldSet,
            autoApplyFileTypes: next
        });
    }
    const handleSwitchToggle = (index, event) => {
        const newFields = [...fieldSet.fields];
        newFields[index].perClient = event.target.checked;
        if (event.target.checked && !newFields[index].identifier) {
            newFields[index].identifier = JSON.stringify({});
            console.log(newFields[index])
        }
        setFieldSet({
            ...fieldSet,
            fields: newFields,
        });
    };
    


    const handleFieldCleared = (index) => {
        const newFields = [...fieldSet.fields];
        newFields.splice(index, 1);
        setFieldSet({
            ...fieldSet,
            fields: newFields,
        });
    };

    const overLimitFieldCount = fieldSet.fields.filter(f => isIntakeFieldNameTooLong(f.name)).length;
    const hasOverLimitField = overLimitFieldCount > 0;

    const handleNewFieldSetSaveClicked = async () => {
        const fSet = { ...fieldSet, fields: fieldSet.fields.filter(f => f.name.trim()) };
        if (fSet.fieldSetName.trim() === '') {
            alert('You must include a field set name.')
        } else if (fSet.fields.length === 0) {
            alert('You must include at least one field.')
        } else if (fSet.fields.some(f => isIntakeFieldNameTooLong(f.name))) {
            alert(`Each intake field name must be ${MAX_INTAKE_FIELD_NAME_LENGTH} characters or fewer.`)
        } else {
            let result = props.editFieldSet ? await updateCustomFieldSet(sessionInfo.sid, fSet, props.editFieldSet) : await saveCustomFieldSet(sessionInfo.sid, fSet);
            if (result) {
                alert('Successfully Saved');
                setFieldSet(getDefaultFieldSet()); // Reset after saving
                props.onHide();
            }
        }
    };

    const onHide = () => {
        for (let i = 0; i < fieldSet.fields.length; i++) {
            handleFieldCleared(i)
        }
        props.onHide();
    }

    useEffect(() => {
        if (clear) {
            setFieldSet(getDefaultFieldSet())
            setClear(false)
        }
    }, [clear])


    return (
        <Modal show={props.show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{props.fromSettings && <>New</>} Intake Template {!props.fromSettings && <>(View Only)</>}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 240px', minWidth: '240px' }}>
                        <Form.Label>Intake Template Name</Form.Label>
                        <Form.Control
                            className="standard-form"
                            name='fieldSetName'
                            value={fieldSet.fieldSetName}
                            onChange={(event) => handleFieldSetNameChange(event)}
                            disabled={!props.fromSettings}
                        />
                    </div>
                    <div style={{ flex: '1 1 300px', minWidth: '240px' }}>
                        <Form.Label>Auto-Apply To File Type</Form.Label>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', paddingTop: '6px' }}>
                            {availableFileTypes.map((fileType) => (
                                <Form.Check
                                    key={`auto-apply-${fileType}`}
                                    type="checkbox"
                                    id={`auto-apply-${fileType}`}
                                    label={fileType}
                                    checked={(fieldSet.autoApplyFileTypes ?? []).includes(fileType)}
                                    onChange={(e) => handleAutoApplyFileTypeToggle(fileType, e.target.checked)}
                                    disabled={!props.fromSettings}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <br />
                {props.fromSettings && hasOverLimitField &&
                    <Alert variant="danger" className="py-2">
                        {overLimitFieldCount === 1
                            ? 'One intake field name is too long. '
                            : `${overLimitFieldCount} intake field names are too long. `}
                        Each field name must be {MAX_INTAKE_FIELD_NAME_LENGTH} characters or fewer, otherwise it cannot be added to a ClientConnect file.
                    </Alert>
                }
                <Table className="table-input" bordered>
                    <thead>
                        <tr>
                            <th>Intake Field Name</th>
                            {fieldSet.fields.length >0 && <th>Per Client</th>}
                            {props.fromSettings && <th></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {fieldSet.fields.map((field, index) => {
                            const tooLong = isIntakeFieldNameTooLong(field.name);
                            return (
                                <tr key={'new-field-' + index}>
                                    <td>
                                        <Form.Control
                                            className="standard-form"
                                            name='fieldSetName'
                                            value={field.name}
                                            onChange={(event) => handleFieldChange(index, event)}
                                            placeholder="enter a field name.."
                                            disabled={!props.fromSettings}
                                            isInvalid={props.fromSettings && tooLong}
                                            aria-describedby={tooLong ? `field-name-length-${index}` : undefined}
                                        />
                                        {props.fromSettings && tooLong &&
                                            <Form.Control.Feedback type="invalid" id={`field-name-length-${index}`} style={{ display: 'block' }}>
                                                {field.name.length} / {MAX_INTAKE_FIELD_NAME_LENGTH} characters. Shorten this field name by {field.name.length - MAX_INTAKE_FIELD_NAME_LENGTH}.
                                            </Form.Control.Feedback>
                                        }
                                    </td>
                                    <td>
                                  <Form.Check 
                                    type="checkbox"
                                    id={`perClientCheckbox-${index}`}
                                    onChange={(event) =>
                                      handleSwitchToggle(index, event)
                                    }
                                    checked={field.perClient}
                                    disabled={!props.fromSettings}
                                  />
                                </td>
                                    {props.fromSettings &&
                                        <td style={{ width: '50px' }}>
                                            <Button
                                                variant="link"
                                                onClick={() => handleFieldCleared(index)}
                                            >
                                                <i className="fa-regular fa-trash-can"></i>
                                            </Button>

                                        </td>
                                    }
                                </tr>
                            )
                        })}
                    </tbody>

                    {props.fromSettings &&
                        <tfoot>
                            <tr>
                                <td colSpan={3}>
                                    <Button
                                        variant='link-text'
                                        onClick={() => addNewField()}
                                    >
                                        + Field
                                    </Button>
                                </td>
                            </tr>
                        </tfoot>
                    }
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="link" onClick={() => {
                    onHide();
                }}>
                    Cancel
                </Button>
                {
                    props.fromSettings &&
                    <Button
                        variant="primary"
                        disabled={hasOverLimitField}
                        title={hasOverLimitField ? `Shorten field names to ${MAX_INTAKE_FIELD_NAME_LENGTH} characters or fewer to save.` : undefined}
                        onClick={() => {
                            setFieldSet(getDefaultFieldSet())
                            handleNewFieldSetSaveClicked()
                        }}
                    >
                        Save
                    </Button>
                }
            </Modal.Footer>
        </Modal>
    )
}
